import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import Navbar from "@/components/site/Navbar";
import Hero from "@/components/site/Hero";
import Nosotros from "@/components/site/Nosotros";
import WhyChoose from "@/components/site/WhyChoose";
import Servicios from "@/components/site/Servicios";
import BlogSection from "@/components/site/BlogSection";
import Footer from "@/components/site/Footer";
import { useLang } from "@/contexts/LanguageContext";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "GAMO Consulting | Continuidad & Resiliencia" },
      { name: "description", content: "GAMO Consulting: planes accionables de continuidad de negocio, gestión de riesgos y resiliencia organizacional para empresas de todos los tamaños." },
      { property: "og:title", content: "GAMO Consulting" },
      { property: "og:description", content: "Tu negocio, siempre activo. Continuidad de negocio y resiliencia organizacional." },
    ],
  }),
  component: Index,
});

function Index() {
  const { lang } = useLang();
  const T =
    lang === "en"
      ? {
          heading: "Contact us",
          intro: "Complete the form and a GAMO consultant will get in touch with you.",
          name: "Full name *",
          email: "Corporate email *",
          company: "Company",
          phone: "Phone",
          countryCode: "Country code",
          interests: "Services of interest",
          services: {
            bc: "Business continuity",
            risk: "Risk management",
            resilience: "Organizational resilience",
            audit: "Audit",
            training: "Training",
            other: "Other",
          },
          message: "Message *",
          submit: "Send message",
          subject: "New contact from GAMO Consulting",
        }
      : {
          heading: "Contáctanos",
          intro: "Completa el formulario y un consultor de GAMO se pondrá en contacto contigo.",
          name: "Nombre completo *",
          email: "Correo corporativo *",
          company: "Empresa",
          phone: "Teléfono",
          countryCode: "Indicativo",
          interests: "Servicios de interés",
          services: {
            bc: "Continuidad de negocio",
            risk: "Gestión de riesgos",
            resilience: "Resiliencia organizacional",
            audit: "Auditoría",
            training: "Capacitación",
            other: "Otro",
          },
          message: "Mensaje *",
          submit: "Enviar mensaje",
          subject: "Nuevo contacto desde GAMO Consulting",
        };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 60);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <Nosotros />
      <WhyChoose />
      <Servicios />
      <BlogSection />
      <section
        id="contacto"
        style={{
          background: "#0E1B2C",
          padding: "80px 24px",
          fontFamily: "Arial, sans-serif",
          color: "#ffffff",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: 36, fontWeight: 700, margin: "0 0 12px" }}>{T.heading}</h2>
          <p style={{ margin: "0 0 32px", color: "#cbd5e1", fontSize: 16 }}>{T.intro}</p>

          <form
            action="https://formspree.io/f/xykvlbwr"
            method="POST"
            style={{ background: "#ffffff", color: "#111111", padding: 32, borderRadius: 12 }}
          >
            <input type="hidden" name="_subject" value={T.subject} />

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="home-nombre" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                {T.name}
              </label>
              <input
                id="home-nombre"
                type="text"
                name="nombre"
                required
                style={{ boxSizing: "border-box", width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="home-email" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                {T.email}
              </label>
              <input
                id="home-email"
                type="email"
                name="email"
                required
                style={{ boxSizing: "border-box", width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="home-empresa" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                {T.company}
              </label>
              <input
                id="home-empresa"
                type="text"
                name="empresa"
                style={{ boxSizing: "border-box", width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                {T.phone}
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  id="home-indicativo"
                  name="indicativo"
                  defaultValue="+57"
                  aria-label={T.countryCode}
                  style={{ flex: "0 0 140px", boxSizing: "border-box", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15, background: "#fff" }}
                >
                  <option value="+57">+57 Colombia</option>
                  <option value="+1">+1 USA / Canada</option>
                  <option value="+52">+52 México</option>
                  <option value="+34">+34 España</option>
                  <option value="+51">+51 Perú</option>
                  <option value="+56">+56 Chile</option>
                  <option value="+58">+58 Venezuela</option>
                  <option value="+593">+593 Ecuador</option>
                  <option value="+591">+591 Bolivia</option>
                  <option value="+595">+595 Paraguay</option>
                  <option value="+598">+598 Uruguay</option>
                  <option value="+54">+54 Argentina</option>
                  <option value="+55">+55 Brasil</option>
                  <option value="+507">+507 Panamá</option>
                  <option value="+506">+506 Costa Rica</option>
                  <option value="+502">+502 Guatemala</option>
                  <option value="+503">+503 El Salvador</option>
                  <option value="+504">+504 Honduras</option>
                  <option value="+505">+505 Nicaragua</option>
                  <option value="+53">+53 Cuba</option>
                  <option value="+1809">+1809 Rep. Dominicana</option>
                  <option value="+44">+44 United Kingdom</option>
                  <option value="+33">+33 France</option>
                  <option value="+49">+49 Germany</option>
                  <option value="+39">+39 Italy</option>
                  <option value="+351">+351 Portugal</option>
                  <option value="+31">+31 Netherlands</option>
                  <option value="+41">+41 Switzerland</option>
                  <option value="+86">+86 China</option>
                  <option value="+81">+81 Japan</option>
                  <option value="+91">+91 India</option>
                  <option value="+61">+61 Australia</option>
                </select>
                <input
                  id="home-telefono"
                  type="tel"
                  name="telefono"
                  style={{ flex: 1, boxSizing: "border-box", width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15 }}
                />
              </div>
            </div>

            <fieldset style={{ marginBottom: 16, border: "1px solid #e5e7eb", borderRadius: 6, padding: 16 }}>
              <legend style={{ fontWeight: 600, padding: "0 6px" }}>{T.interests}</legend>
              <label style={{ display: "block", marginBottom: 8 }}>
                <input type="checkbox" name="servicios" value={T.services.bc} style={{ marginRight: 8 }} />
                {T.services.bc}
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                <input type="checkbox" name="servicios" value={T.services.risk} style={{ marginRight: 8 }} />
                {T.services.risk}
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                <input type="checkbox" name="servicios" value={T.services.resilience} style={{ marginRight: 8 }} />
                {T.services.resilience}
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                <input type="checkbox" name="servicios" value={T.services.audit} style={{ marginRight: 8 }} />
                {T.services.audit}
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                <input type="checkbox" name="servicios" value={T.services.training} style={{ marginRight: 8 }} />
                {T.services.training}
              </label>
              <label style={{ display: "block" }}>
                <input type="checkbox" name="servicios" value={T.services.other} style={{ marginRight: 8 }} />
                {T.services.other}
              </label>
            </fieldset>

            <div style={{ marginBottom: 20 }}>
              <label htmlFor="home-mensaje" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                {T.message}
              </label>
              <textarea
                id="home-mensaje"
                name="mensaje"
                rows={5}
                required
                style={{ boxSizing: "border-box", width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 15, fontFamily: "inherit" }}
              />
            </div>

            <button
              type="submit"
              style={{
                background: "#00C853",
                color: "#ffffff",
                padding: "12px 28px",
                border: "none",
                borderRadius: 6,
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {T.submit}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
